package ai.jessie.web.onboarding

import ai.jessie.shared.onboarding.EntryDestination
import ai.jessie.shared.onboarding.OrganisationChoiceResponse
import ai.jessie.shared.onboarding.SelectOrganisationRequest
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.ensureActive
import kotlinx.coroutines.launch

data class OrganisationSwitchUiState(
    val open: Boolean = false,
    val choices: List<OrganisationChoiceResponse>? = null,
    val pendingDiscard: OrganisationChoiceResponse? = null,
    val activeRevoked: Boolean = false,
    val selectedOrganisationId: String? = null,
    val busy: Boolean = false,
    val error: OnboardingProblem? = null,
)

data class OrganisationSwitchActions(
    val open: () -> Unit,
    val refresh: () -> Unit,
    val choose: (OrganisationChoiceResponse) -> Unit,
    val confirmDiscard: () -> Unit,
    val keepChanges: () -> Unit,
    val cancel: () -> Unit,
)

/**
 * UC-004 switch from an active organisation. Listing is a read-only proposal; only a confirmed
 * choice is sent to select, which remembers it. [hasUnsavedChanges] is the seam for future
 * organisation-scoped editors (BR-021); the page owns their state, so navigating to the committed
 * organisation disposes it (BR-017).
 */
class OrganisationSwitchState(
    private val selectionPort: OrganisationSelectionPort,
    private val onboardingPort: OnboardingPort,
    private val scope: CoroutineScope,
    private val activeOrganisationId: String,
    private val hasUnsavedChanges: () -> Boolean = { false },
) {
    var state by mutableStateOf(OrganisationSwitchUiState())
        private set

    val actions =
        OrganisationSwitchActions(
            open = {
                if (!state.open) {
                    state = state.copy(open = true)
                    refresh()
                }
            },
            refresh = ::refresh,
            choose = ::choose,
            confirmDiscard = { state.pendingDiscard?.let(::commit) },
            keepChanges = {
                if (!state.busy) state = state.copy(open = false, pendingDiscard = null)
            },
            cancel = {
                if (!state.busy)
                    state =
                        OrganisationSwitchUiState(
                            activeRevoked = state.activeRevoked,
                            error = state.error?.takeIf { it.authenticationRequired },
                        )
            },
        )

    private fun refresh() = request {
        state = state.copy(choices = null, pendingDiscard = null)
        loadChoices()
    }

    private fun choose(choice: OrganisationChoiceResponse) {
        if (state.busy || state.choices?.contains(choice) != true) return
        if (isActive(choice.organisationId)) return
        if (hasUnsavedChanges()) state = state.copy(pendingDiscard = choice, error = null)
        else commit(choice)
    }

    private fun commit(choice: OrganisationChoiceResponse) = request {
        val entry =
            onboardingPort
                .select(
                    SelectOrganisationRequest(choice.organisationId, choice.role, choice.version)
                )
                .checked()
        currentCoroutineContext().ensureActive()
        require(
            entry.destination == EntryDestination.ORGANISATION &&
                entry.organisationId.equals(choice.organisationId, ignoreCase = true)
        )
        state =
            state.copy(
                selectedOrganisationId = entry.organisationId,
                choices = null,
                pendingDiscard = null,
            )
    }

    private suspend fun loadChoices() {
        val choices = selectionPort.organisations().organisations
        currentCoroutineContext().ensureActive()
        choices.forEach { require(validOrganisationId(it.organisationId)) }
        state =
            state.copy(
                choices = choices,
                activeRevoked = choices.none { isActive(it.organisationId) },
            )
    }

    // Single-flight; disposed page scopes cannot publish late success, error or finalisation.
    private fun request(block: suspend () -> Unit) {
        if (state.busy) return
        state = state.copy(busy = true, error = null)
        scope.launch {
            try {
                block()
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: OnboardingRequestFailure) {
                currentCoroutineContext().ensureActive()
                state = state.copy(choices = null, pendingDiscard = null, error = failure.problem())
                if (failure.refreshChoices()) refreshRejectedChoice()
            } catch (_: Exception) {
                currentCoroutineContext().ensureActive()
                state =
                    state.copy(choices = null, pendingDiscard = null, error = unavailableProblem())
            } finally {
                currentCoroutineContext().ensureActive()
                state = state.copy(busy = false)
            }
        }
    }

    private suspend fun refreshRejectedChoice() {
        try {
            loadChoices()
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (failure: OnboardingRequestFailure) {
            currentCoroutineContext().ensureActive()
            state = state.copy(error = failure.problem())
        } catch (_: Exception) {
            currentCoroutineContext().ensureActive()
            state = state.copy(error = unavailableProblem())
        }
    }

    private fun isActive(organisationId: String) =
        organisationId.equals(activeOrganisationId, ignoreCase = true)
}
